import React, { useCallback, useEffect, useRef, useState } from "react";
import { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { IProductDetailImage } from "../../interfaces/shopify";
import {
  NextDetailButton,
  PrevDetailButton,
  usePrevDetailNextButtons,
} from "./components/EmblaDetailButton";
import "../../css/EmblaDetail.css";
import {
  Box,
  CardActionArea,
  CardMedia,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  SelectedSnapDisplay,
  useSelectedSnapDisplay,
} from "./components/SelectedSnapDisplay";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

type PropType = {
  category?: string;
  slides: IProductDetailImage[];
  options?: EmblaOptionsType;
  ref: React.RefObject<HTMLDivElement | null>;
};

export const EmblaDetail: React.FC<PropType> = ({ slides, options, ref }) => {
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel(options);
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: "keepSnaps",
    duration: 20,
    dragFree: true,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevDetailNextButtons(emblaMainApi);

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return;

    const selected = emblaMainApi.selectedScrollSnap();
    setSelectedIndex(selected);

    emblaThumbsApi.scrollTo(selected + 1);
  }, [emblaMainApi, emblaThumbsApi]);

  useEffect(() => {
    if (!emblaMainApi) return;
    emblaMainApi.on("select", onSelect).on("reInit", onSelect);
    onSelect();
  }, [emblaMainApi, onSelect]);

  const scrollTo = useCallback(
    (index: number) => emblaMainApi && emblaMainApi.scrollTo(index),
    [emblaMainApi]
  );

  const { selectedSnap, snapCount } = useSelectedSnapDisplay(emblaMainApi);
  const emblaDetailtRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isTableAndMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (!emblaDetailtRef.current) return;

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: emblaDetailtRef.current,
        endTrigger: ref.current,
        start: "top top+=70",
        end: "bottom bottom",
        pin: true,
        scrub: true,
      });
    }, emblaDetailtRef);

    return () => ctx.revert();
  }, [ref]);

  return (
    <Box ref={isTableAndMobile ? null : emblaDetailtRef}>
      <section className="embla__detail">
        <div className="embla__viewport__detail" ref={emblaMainRef}>
          <div className="embla__container__detail">
            {slides?.map((img, i) => (
              <div className="embla__slide__detail" key={i}>
                <CardMedia
                  component="img"
                  width="100%"
                  height="100%"
                  loading="lazy"
                  image={img.node.url}
                  alt={img.node.altText ?? ""}
                />
              </div>
            ))}
          </div>
          <Box className="embla__controls__detail">
            <Box className="controls">
              <PrevDetailButton
                onClick={onPrevButtonClick}
                disabled={prevBtnDisabled}
              />
              <SelectedSnapDisplay
                selectedSnap={selectedSnap}
                snapCount={snapCount}
              />
              <NextDetailButton
                onClick={onNextButtonClick}
                disabled={nextBtnDisabled}
              />
            </Box>
          </Box>
        </div>

        <div className="embla-thumbs">
          <div className="embla-thumbs__viewport" ref={emblaThumbsRef}>
            <div className="embla-thumbs__container">
              <Box
                sx={{
                  minWidth:
                    slides.length <= 1
                      ? 220
                      : slides.length <= 2
                      ? 160
                      : slides.length <= 3
                      ? 130
                      : 60,
                }}
              />
              {slides?.map((img, i) => (
                <CardActionArea
                  key={i}
                  className={`embla-thumbs__slide ${
                    i === selectedIndex ? "is-selected" : ""
                  }`}
                  onClick={() => scrollTo(i)}
                >
                  <img
                    className="embla-thumbs__slide__img"
                    src={img.node.url}
                    alt={img.node.altText ?? ""}
                  />
                </CardActionArea>
              ))}
              <Box sx={{ minWidth: 70 }} />
            </div>
          </div>
        </div>
      </section>
    </Box>
  );
};
